import { colors } from "@zfp-akademie-a-s/zfp-design";
import type { ModelingTimelinePoint, PensionGoalModelingPreview } from "Infrastructure/ApiZfp/Schema";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";

export type PensionChartData = {
  readonly accumulationOptimistic: number[];
  readonly accumulationRealistic: number[];
  readonly accumulationPesimistic: number[];
  readonly pensionWithdrawOptimistic: number[];
  readonly pensionWithdrawRealistic: number[];
  readonly pensionWithdrawPesimistic: number[];
  readonly retirementMonth: number;
  readonly totalMonths: number;
  // First month the capital runs out; null for an infinite pension, which never depletes.
  readonly depletionMonth: number | null;
};

const _toValuesByMonth = (timeline: ModelingTimelinePoint[]) => {
  const values: number[] = [];
  for (const point of timeline) {
    values[point.month] = point.value;
  }

  return values;
};

export const mapPensionPreviewToChartData = (preview: PensionGoalModelingPreview): PensionChartData | null => {
  const { retirementMonth } = preview;
  const realistic = _toValuesByMonth(preview.timelines.realistic);

  if (retirementMonth === null || realistic.length === 0) return null;

  const optimistic = _toValuesByMonth(preview.timelines.optimistic);
  const pesimistic = _toValuesByMonth(preview.timelines.pessimistic);

  // Both phases share one x axis: accumulation stops at the retirement month, and the withdrawal series is
  // padded with NaN up to that point so it picks up exactly where accumulation left off.
  const accumulation = (values: number[]) => values.slice(0, retirementMonth + 1);
  const withdraw = (values: number[]) => new Array(retirementMonth).fill(Number.NaN).concat(values.slice(retirementMonth));

  const depletionMonth = realistic.findIndex((value, month) => month > retirementMonth && value < 0);

  return {
    accumulationOptimistic: accumulation(optimistic),
    accumulationRealistic: accumulation(realistic),
    accumulationPesimistic: accumulation(pesimistic),
    pensionWithdrawOptimistic: withdraw(optimistic),
    pensionWithdrawRealistic: withdraw(realistic),
    pensionWithdrawPesimistic: withdraw(pesimistic),
    retirementMonth,
    totalMonths: realistic.length,
    depletionMonth: depletionMonth === -1 ? null : depletionMonth,
  };
};

export const getPensionChartAnnotations = ({
  data,
}: {
  data: PensionChartData;
}) => {
  const targetAmountPesimistic = data.accumulationPesimistic[data.retirementMonth];
  const targetAmountOptimistic = data.accumulationOptimistic[data.retirementMonth];

  const commonLineProps = {
    type: "line" as const,
    borderColor: colors.borderLight,
    borderWidth: 1,
    borderDash: [3, 5],
    xMin: 0,
    xMax: data.totalMonths - 1,
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    font: {
      weight: 400,
      size: 10,
    },
  };

  return {
    pesimisticLine: {
      ...commonLineProps,
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmountPesimistic),
      },
      yMin: targetAmountPesimistic,
      yMax: targetAmountPesimistic,
    },
    optimisticLine: {
      ...commonLineProps,
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmountOptimistic),
      },
      yMin: targetAmountOptimistic,
      yMax: targetAmountOptimistic,
    },
  };
};
